import { motion } from 'framer-motion';
import { useGameStore } from '@/game';
import { Trophy, RotateCw, Home, Star, Bike } from 'lucide-react';
interface GameOverScreenProps {
  onRestart: () => void;
  onHome: () => void;
}
export function GameOverScreen({ onRestart, onHome }: GameOverScreenProps) {
  const score = useGameStore((s) => s.score);
  const highScore = useGameStore((s) => s.highScore);
  const speed = useGameStore((s) => s.speed);
  const isNewHighScore = score >= highScore && score > 0;
  const displayBest = Math.max(score, highScore);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute inset-0 z-30 bg-black/80 backdrop-blur-md flex items-center justify-center px-6"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 40 }}
        transition={{ type: 'spring', damping: 22, stiffness: 280, delay: 0.15 }}
        className="w-full max-w-sm bg-gradient-to-b from-amber-50 to-orange-100 rounded-3xl p-7 shadow-2xl border-4 border-amber-200/80"
      >
        <motion.div
          initial={{ rotate: -15, scale: 0.3 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ type: 'spring', damping: 12, stiffness: 180, delay: 0.3 }}
          className="flex justify-center mb-2"
        >
          <div
            className={`rounded-full p-4 shadow-xl ${
              isNewHighScore ? 'bg-yellow-400' : 'bg-amber-700'
            }`}
          >
            {isNewHighScore ? (
              <Trophy className="w-10 h-10 text-white" strokeWidth={2.5} />
            ) : (
              <Bike className="w-10 h-10 text-white" strokeWidth={2.5} />
            )}
          </div>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-3xl font-black text-amber-900 text-center leading-tight"
        >
          {isNewHighScore ? 'New Record!' : 'Ride Over'}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="text-amber-700 text-center text-sm font-bold mt-1"
        >
          {isNewHighScore ? 'Personal best smashed!' : 'Better luck next ride'}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-5 bg-white/70 rounded-2xl p-4 space-y-3"
        >
          <Row label="Final Score" value={score.toLocaleString()} highlight />
          <Row label="Top Speed" value={`${speed.toFixed(1)}x`} />
          <Row
            label="Best"
            value={displayBest.toLocaleString()}
            color="text-amber-600"
            icon={<Star className="w-4 h-4 inline -mt-1" strokeWidth={2.5} />}
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="flex flex-col gap-3 mt-5"
        >
          <motion.button
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.02 }}
            onClick={onRestart}
            className="bg-amber-500 active:bg-amber-600 text-white font-black text-lg py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 min-h-[56px] transition-colors"
          >
            <RotateCw className="w-6 h-6" strokeWidth={2.5} />
            Ride Again
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.02 }}
            onClick={onHome}
            className="bg-stone-700 active:bg-stone-800 text-white font-bold text-base py-3 rounded-2xl shadow flex items-center justify-center gap-2 min-h-[48px] transition-colors"
          >
            <Home className="w-5 h-5" strokeWidth={2.5} />
            Back to Lobby
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
function Row({
  label,
  value,
  highlight,
  color,
  icon,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  color?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-amber-800 font-bold text-sm uppercase tracking-wider">
        {label}
      </span>
      <span
        className={`font-black tabular-nums ${
          highlight ? 'text-3xl text-amber-900' : 'text-xl'
        } ${color ?? ''}`}
      >
        {icon}
        {value}
      </span>
    </div>
  );
}